import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Navigation, type Locale } from './components/Navigation';
import { SiteFooter } from './components/SiteFooter';
import { siteConfig } from './siteConfig';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Gift,
  ListChecks,
  Mail,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
} from 'lucide-react';

const copy = {
  sv: {
    eyebrow: 'En familjeapp från keevo',
    title: 'Mindre tjat. Mer tydlighet tillsammans.',
    intro:
      'FamPo samlar familjens uppgifter, poäng och egna belöningar på familjens Android-telefoner. Barn markerar klart och en vuxen godkänner.',
    cta: 'Kommer till Google Play',
    category: 'Android först',
    noAds: 'Inga annonser',
    languages: 'Svenska, engelska och arabiska',
    howEyebrow: 'Så fungerar FamPo',
    howTitle: 'Ett enkelt flöde för hela familjen.',
    steps: [
      ['Vuxna skapar', 'Lägg in dagliga, veckovisa eller egna uppgifter och välj poäng.'],
      ['Barn markerar klart', 'Varje barn har en tydlig profil med sina tilldelade uppgifter och poäng.'],
      ['Vuxna godkänner', 'Poäng delas ut först efter att en vuxen har granskat uppgiften.'],
      ['Familjen väljer belöningar', 'Poängen kan sparas eller användas till belöningar som familjen själv bestämmer.'],
    ],
    featuresEyebrow: 'Byggd för vardagen',
    featuresTitle: 'Motivation utan att tappa vuxenkontrollen.',
    features: [
      ['Tydliga uppgifter', 'Dagens och veckans uppgifter samlade i barnets egen vy.'],
      ['Poäng och belöningar', 'Familjen bestämmer både värdet på uppgifterna och vad poängen kan användas till.'],
      ['Godkännande först', 'En vuxen godkänner eller nekar innan poängen delas ut.'],
      ['Barnvy och vuxenläge', 'Barnet får en begränsad vy för sin profil medan vuxna hanterar familjen i föräldraläget.'],
      ['Familjen följer poängen', 'Barn och vuxna kan se poäng och uppgiftsstatus i sina respektive vyer.'],
      ['Ett familjekonto', 'Separata profiler synkas mellan familjens Android-telefoner.'],
    ],
    galleryEyebrow: 'I appen',
    galleryTitle: 'Se hur FamPo fungerar.',
    galleryIntro: 'Vyer från den nuvarande Android-versionen under releaseförberedelse. Svep eller använd pilarna för att utforska.',
    screenshotLabels: [
      ['Barnets hem', 'Uppgifter, nivå, poäng och mål på ett ställe.'],
      ['Belöningar', 'Familjens egen belöningsbutik.'],
      ['Statistik', 'En översikt från den nuvarande Android-versionen.'],
      ['Föräldraöversikt', 'Överblick över barnens poäng och inställningar.'],
      ['Hantera uppgifter', 'Skapa, tilldela, pausa och redigera.'],
      ['Topplista', 'En lekfull familjeöversikt i den nuvarande Android-versionen.'],
      ['Notiser', 'Familjens notis- och meddelandevy.'],
      ['Godkännande', 'Granska barnets uppgift innan poängen delas ut.'],
    ],
    trustEyebrow: 'Tryggt familjeverktyg',
    trustTitle: 'Barnprofiler – inte öppna barnkonton.',
    trustBody:
      'En vuxen skapar och ansvarar för familjekontot. Barnprofiler hanteras inom familjen, föräldraläget skyddas med PIN och FamPo har varken annonser eller offentlig chatt.',
    trustPoints: ['Vuxenstyrt familjekonto', 'PIN-skyddat föräldraläge', 'Ingen offentlig kontakt mellan familjer', 'Familjedata säljs inte'],
    supportEyebrow: 'Support',
    supportTitle: 'Frågor om FamPo?',
    supportBody: 'Kontakta keevo så hjälper vi dig med konto, installation, funktioner eller integritet.',
    privacy: 'Integritetspolicy',
    playNote: 'Google Play-länken aktiveras när den offentliga butikssidan är publicerad.',
    nav: ['Så fungerar', 'Funktioner', 'Skärmbilder', 'Support'],
  },
  en: {
    eyebrow: 'A family app by keevo',
    title: 'Less nagging. More clarity together.',
    intro:
      "FamPo keeps your family's tasks, points and own rewards in sync across the family's Android phones. Children mark tasks complete and an adult reviews them.",
    cta: 'Coming to Google Play',
    category: 'Android first',
    noAds: 'No ads',
    languages: 'Swedish, English and Arabic',
    howEyebrow: 'How FamPo works',
    howTitle: 'One simple flow for the whole family.',
    steps: [
      ['Adults create', 'Add daily, weekly or custom tasks and decide how many points they are worth.'],
      ['Children mark complete', 'Each child has a clear profile with assigned tasks and points.'],
      ['Adults approve', 'Points are awarded only after an adult has reviewed the task.'],
      ['The family chooses rewards', 'Points can be saved or used for rewards chosen by the family.'],
    ],
    featuresEyebrow: 'Made for everyday life',
    featuresTitle: 'Motivation without giving up adult control.',
    features: [
      ['Clear tasks', "Today's and this week's tasks in the child's own view."],
      ['Points and rewards', 'The family decides what tasks are worth and how points may be used.'],
      ['Approval first', 'An adult approves or rejects before points are awarded.'],
      ['Child view and parent mode', 'The child gets a restricted profile view while adults manage the family in parent mode.'],
      ['Shared point visibility', 'Children and adults can see points and task status in their respective views.'],
      ['One family account', "Separate profiles stay in sync across the family's Android phones."],
    ],
    galleryEyebrow: 'Inside the app',
    galleryTitle: 'See how FamPo works.',
    galleryIntro: 'Views from the current Android version in release preparation. Swipe or use the arrows to explore.',
    screenshotLabels: [
      ["Child's home", 'Tasks, level, points and goals in one place.'],
      ['Rewards', "The family's own reward shop."],
      ['Statistics', 'An overview from the current Android version.'],
      ['Parent overview', "An overview of the children's points and settings."],
      ['Manage tasks', 'Create, assign, pause and edit.'],
      ['Leaderboard', 'A playful family overview in the current Android version.'],
      ['Notifications', "The family's notification and message view."],
      ['Approval', "Review a child's task before points are awarded."],
    ],
    trustEyebrow: 'A safer family tool',
    trustTitle: 'Child profiles – not open child accounts.',
    trustBody:
      'An adult creates and manages the family account. Child profiles stay within the family, parent mode is protected by a PIN, and FamPo has no ads or public chat.',
    trustPoints: ['Adult-managed family account', 'PIN-protected parent mode', 'No public contact between families', 'Family data is not sold'],
    supportEyebrow: 'Support',
    supportTitle: 'Questions about FamPo?',
    supportBody: 'Contact keevo for help with your account, installation, features or privacy.',
    privacy: 'Privacy policy',
    playNote: 'The Google Play link will be enabled when the public store listing is live.',
    nav: ['How it works', 'Features', 'Screenshots', 'Support'],
  },
} as const;

const screenshots = [
  '01-barnets-hem.jpg',
  '02-beloningar.jpg',
  '03-statistik.jpg',
  '04-foralder-oversikt.jpg',
  '05-foralder-uppgifter.jpg',
  '06-topplista.jpg',
  '07-notiser.jpg',
  '08-godkannande.jpg',
];

const featureIcons = [ListChecks, Gift, CheckCircle2, ShieldCheck, Star, Users];

export default function FampoPage() {
  const [locale] = useState<Locale>(() =>
    window.location.pathname.startsWith('/en/fampo') ? 'en' : 'sv'
  );
  const [activeImage, setActiveImage] = useState(0);
  const reduceMotion = useReducedMotion();
  const t = copy[locale];

  useEffect(() => {
    document.documentElement.lang = locale === 'sv' ? 'sv-SE' : 'en';
    document.title = locale === 'sv'
      ? 'FamPo – Familjeapp för uppgifter, poäng & belöningar | Keevo'
      : 'FamPo – Family Tasks, Points & Rewards App | Keevo';
    const description = locale === 'sv'
      ? 'FamPo är en familjeapp för uppgifter, poäng och belöningar. Barn markerar klart, vuxna godkänner och familjen följer framsteg tillsammans.'
      : 'FamPo is a family app for tasks, points and rewards. Children mark tasks complete, adults approve them and the family follows progress together.';
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, [locale]);

  const moveGallery = (direction: number) => {
    setActiveImage((current) => (current + direction + screenshots.length) % screenshots.length);
  };

  const changeLocale = (nextLocale: Locale) => {
    if (nextLocale === locale) return;
    const nextPath = nextLocale === 'en' ? '/en/fampo/' : '/fampo/';
    window.location.assign(`${nextPath}${window.location.hash}`);
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F7F3FF] text-[#21143D] font-manrope">
      <div className="grain-overlay" aria-hidden />

      <Navigation locale={locale} onLocaleChange={changeLocale} />

      <nav
        className="sticky top-24 z-40 border-y border-[#DED5F3]/80 bg-[#F7F3FF]/94 backdrop-blur-xl"
        aria-label={locale === 'sv' ? 'FamPo-navigation' : 'FamPo navigation'}
      >
        <div className="mx-auto flex max-w-[1440px] items-center gap-1 overflow-x-auto px-5 py-2 md:px-10 lg:px-16">
          <span className="mr-3 shrink-0 font-sora text-sm font-extrabold text-[#2D1457]">FamPo</span>
          {[
            ['how', t.nav[0]],
            ['features', t.nav[1]],
            ['gallery', t.nav[2]],
            ['support', t.nav[3]],
          ].map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              className="shrink-0 rounded-full px-3 py-2 text-xs font-bold text-[#6B5A80] transition hover:bg-white hover:text-[#7C3AED]"
            >
              {label}
            </a>
          ))}
        </div>
      </nav>

      <main>
        <section className="relative px-5 pb-24 pt-36 md:px-10 md:pb-32 md:pt-44 lg:px-16">
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            <div className="absolute -right-24 top-24 h-96 w-96 rounded-full bg-[#EC4899]/10 blur-3xl" />
            <div className="absolute -left-24 top-72 h-80 w-80 rounded-full bg-[#7C3AED]/15 blur-3xl" />
          </div>
          <div className="relative mx-auto grid max-w-[1440px] items-center gap-16 lg:grid-cols-[1.05fr_.95fr]">
            <motion.div
              initial={{ opacity: 0, y: reduceMotion ? 0 : 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.7 }}
            >
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#DACDF4] bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-[#6D28D9]">
                <Sparkles size={15} aria-hidden /> {t.eyebrow}
              </span>
              <div className="mb-8 flex items-center gap-5">
                <img src="/fampo/app-icon.png" alt="FamPo" className="h-20 w-20 rounded-[24px] shadow-xl shadow-[#7C3AED]/20" />
                <div>
                  <p className="font-sora text-4xl font-extrabold tracking-[-0.04em] text-[#2D1457]">FamPo</p>
                  <p className="mt-1 text-sm font-semibold text-[#7D6A9D]">Familjepoäng · Family Points</p>
                </div>
              </div>
              <h1 className="max-w-[760px] font-sora text-[48px] font-extrabold leading-[1.02] tracking-[-0.045em] text-[#261047] md:text-[68px] lg:text-[76px]">
                {t.title}
              </h1>
              <p className="mt-7 max-w-[650px] text-lg leading-8 text-[#65527F] md:text-xl">{t.intro}</p>
              <div className="mt-9 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-[#2D1457] px-5 py-3 text-sm font-bold text-white">
                  <ExternalLink size={17} aria-hidden /> {t.cta}
                </span>
                {[t.category, t.noAds, t.languages].map((label) => (
                  <span key={label} className="inline-flex items-center rounded-full border border-[#DCD1ED] bg-white/70 px-4 py-3 text-sm font-semibold text-[#594471]">{label}</span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.94, rotate: reduceMotion ? 0 : 2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.9, delay: 0.1 }}
              className="relative mx-auto w-full max-w-[520px]"
            >
              <div className="absolute inset-8 rounded-[42px] bg-gradient-to-br from-[#7C3AED] to-[#EC4899] blur-2xl opacity-25" aria-hidden />
              <div className="relative mx-auto w-[72%] rounded-[42px] border-[9px] border-[#24133D] bg-[#24133D] p-1 shadow-2xl shadow-[#4B2178]/25">
                <img src="/fampo/screenshots/01-barnets-hem.jpg" alt={t.screenshotLabels[0][1]} className="block w-full rounded-[30px]" />
              </div>
              <div className="absolute -bottom-6 left-0 rounded-[22px] border border-[#E4D9F1] bg-white p-4 shadow-xl md:left-2">
                <div className="flex items-center gap-3">
                  <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[#F3E8FF] text-[#7C3AED]"><CheckCircle2 size={23} /></div>
                  <div><p className="text-xs font-bold uppercase tracking-wider text-[#9A89B0]">{locale === 'sv' ? 'Kärnflöde' : 'Core flow'}</p><p className="font-sora text-sm font-bold text-[#321951]">{locale === 'sv' ? 'Klar → granska → poäng' : 'Done → review → points'}</p></div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section id="how" className="scroll-mt-40 bg-white px-5 py-24 md:px-10 md:py-32 lg:px-16">
          <div className="mx-auto max-w-[1280px]">
            <SectionHeading eyebrow={t.howEyebrow} title={t.howTitle} />
            <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {t.steps.map(([title, body], index) => (
                <article key={title} className="rounded-[28px] border border-[#E9E3F0] bg-[#FCFAFF] p-7">
                  <span className="mb-7 grid h-11 w-11 place-items-center rounded-2xl bg-[#7C3AED] font-sora font-bold text-white">{index + 1}</span>
                  <h3 className="font-sora text-xl font-bold text-[#2D1457]">{title}</h3>
                  <p className="mt-3 leading-7 text-[#6B5A80]">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="features" className="scroll-mt-40 px-5 py-24 md:px-10 md:py-32 lg:px-16">
          <div className="mx-auto max-w-[1280px]">
            <SectionHeading eyebrow={t.featuresEyebrow} title={t.featuresTitle} />
            <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {t.features.map(([title, body], index) => {
                const Icon = featureIcons[index];
                return (
                  <article key={title} className="group rounded-[30px] border border-[#DED5EA] bg-white p-8 transition hover:-translate-y-1 hover:border-[#A78BFA] hover:shadow-xl hover:shadow-[#7C3AED]/10">
                    <div className="mb-7 grid h-14 w-14 place-items-center rounded-[20px] bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white"><Icon size={25} aria-hidden /></div>
                    <h3 className="font-sora text-xl font-bold text-[#2D1457]">{title}</h3>
                    <p className="mt-3 leading-7 text-[#6B5A80]">{body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="gallery" className="scroll-mt-40 overflow-hidden bg-[#25113F] px-5 py-24 text-white md:px-10 md:py-32 lg:px-16">
          <div className="mx-auto max-w-[1440px]">
            <SectionHeading eyebrow={t.galleryEyebrow} title={t.galleryTitle} dark />
            <p className="mt-5 max-w-2xl text-lg leading-8 text-[#C7B8D8]">{t.galleryIntro}</p>

            <div className="mt-12 flex items-center justify-between gap-4">
              <div aria-live="polite">
                <p className="font-sora text-xl font-bold">{t.screenshotLabels[activeImage][0]}</p>
                <p className="mt-1 text-[#BFAFD0]">{t.screenshotLabels[activeImage][1]}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => moveGallery(-1)} className="grid h-12 w-12 place-items-center rounded-full border border-white/20 text-white transition hover:bg-white hover:text-[#25113F]" aria-label={locale === 'sv' ? 'Föregående bild' : 'Previous image'}><ChevronLeft /></button>
                <button onClick={() => moveGallery(1)} className="grid h-12 w-12 place-items-center rounded-full border border-white/20 text-white transition hover:bg-white hover:text-[#25113F]" aria-label={locale === 'sv' ? 'Nästa bild' : 'Next image'}><ChevronRight /></button>
              </div>
            </div>

            <div className="mt-9 overflow-x-auto pb-7 [scrollbar-color:#7C3AED_transparent]" role="region" aria-label={t.galleryTitle}>
              <div className="flex w-max gap-5">
                {screenshots.map((file, index) => (
                  <button
                    key={file}
                    type="button"
                    onClick={() => setActiveImage(index)}
                    className={`w-[230px] shrink-0 overflow-hidden rounded-[28px] border-4 bg-[#160925] p-1 text-left transition md:w-[270px] ${index === activeImage ? 'border-[#F472B6] opacity-100' : 'border-transparent opacity-60 hover:opacity-90'}`}
                    aria-label={`${t.screenshotLabels[index][0]}: ${t.screenshotLabels[index][1]}`}
                    aria-pressed={index === activeImage}
                  >
                    <img src={`/fampo/screenshots/${file}`} alt={t.screenshotLabels[index][1]} loading={index < 3 ? 'eager' : 'lazy'} className="w-full rounded-[21px]" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white px-5 py-24 md:px-10 md:py-32 lg:px-16">
          <div className="mx-auto grid max-w-[1280px] gap-12 rounded-[38px] border border-[#E5DDF0] bg-[#F8F5FC] p-8 md:p-14 lg:grid-cols-[1fr_.85fr] lg:p-16">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-[#7C3AED]">{t.trustEyebrow}</span>
              <h2 className="mt-5 font-sora text-4xl font-bold leading-tight tracking-[-0.035em] text-[#2D1457] md:text-5xl">{t.trustTitle}</h2>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-[#68577D]">{t.trustBody}</p>
            </div>
            <div className="grid content-center gap-3">
              {t.trustPoints.map((point) => (
                <div key={point} className="flex items-center gap-3 rounded-2xl bg-white px-5 py-4 text-[#493463] shadow-sm">
                  <ShieldCheck className="shrink-0 text-[#7C3AED]" size={21} aria-hidden />
                  <span className="font-semibold">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="support" className="scroll-mt-40 px-5 py-24 md:px-10 md:py-32 lg:px-16">
          <div className="mx-auto max-w-[1000px] text-center">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-[#7C3AED]">{t.supportEyebrow}</span>
            <h2 className="mt-5 font-sora text-4xl font-bold tracking-[-0.035em] text-[#2D1457] md:text-5xl">{t.supportTitle}</h2>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-[#68577D]">{t.supportBody}</p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <a href={locale === 'sv' ? '/fampo/support/' : '/en/fampo/support/'} className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[#7C3AED] px-6 font-bold text-white transition hover:bg-[#6D28D9]">
                <Mail size={18} /> {locale === 'sv' ? 'Supportcenter' : 'Support center'}
              </a>
              <a href={locale === 'sv' ? '/fampo/privacy/' : '/en/fampo/privacy/'} className="inline-flex min-h-12 items-center gap-2 rounded-full border border-[#D9CDEA] bg-white px-6 font-bold text-[#503A69] transition hover:border-[#7C3AED]">
                {t.privacy} <ExternalLink size={16} />
              </a>
              <a href={locale === 'sv' ? '/fampo/delete-account/' : '/en/fampo/delete-account/'} className="inline-flex min-h-12 items-center gap-2 rounded-full border border-[#D9CDEA] bg-white px-6 font-bold text-[#503A69] transition hover:border-[#7C3AED]">
                {locale === 'sv' ? 'Radera konto' : 'Delete account'}
              </a>
            </div>
            <p className="mt-5 text-sm text-[#8A789F]">
              {locale === 'sv' ? 'Direkt e-post: ' : 'Direct email: '}
              <a href={`mailto:${siteConfig.email}`} className="font-bold text-[#7C3AED]">{siteConfig.email}</a>
            </p>
            <p id="google-play" className="mt-8 text-sm text-[#8A789F]">{t.playNote}</p>
          </div>
        </section>
      </main>

      <SiteFooter locale={locale} productContext="fampo" />
    </div>
  );
}

function SectionHeading({ eyebrow, title, dark = false }: { eyebrow: string; title: string; dark?: boolean }) {
  return (
    <div className="max-w-[820px]">
      <span className={`text-xs font-bold uppercase tracking-[0.14em] ${dark ? 'text-[#F472B6]' : 'text-[#7C3AED]'}`}>{eyebrow}</span>
      <h2 className={`mt-5 font-sora text-4xl font-bold leading-tight tracking-[-0.035em] md:text-5xl ${dark ? 'text-white' : 'text-[#2D1457]'}`}>{title}</h2>
    </div>
  );
}
